import { number, object, string } from "yup";

import { ListPriceFormData } from "@/components/screens/ListPriceFormPage/types";
import { IListPrice, IListPriceInput } from "@/interfaces/list-price.interface";
import { ListPriceFormMessages } from "@/messages/listPrice/list-price-form.messages";
import { ToastProps } from "@/types/toast.types";

export const buildInitialData = (listPrice: IListPrice | null): ListPriceFormData => {
  return {
    productId: listPrice?.productId || 0,
    productCode: listPrice?.productCode || "",
    mainPrice: listPrice?.mainPrice || 0,
    promoPrice: listPrice?.promoPrice || 0,
    startPromoPrice: listPrice?.startPromoPrice || null,
    endPromoPrice: listPrice?.endPromoPrice || null,
    active: listPrice?.active || false,
    code: listPrice?.code || "",
    kit: listPrice?.kit || false,
    title: listPrice?.title || "",
    ean: listPrice?.ean || "",
    variations: listPrice?.variations || [],
    stock: listPrice?.stock || 0,
  };
};

export const buildFormSchema = (messages: ListPriceFormMessages) =>
  object().shape({
    mainPrice: number()
      .required(messages.errorRequiredMainPrice)
      .min(0.01, messages.errorMainPriceGreaterThanZero),

    startPromoPrice: string()
      .nullable()
      .test(
        "is-before-endPromoPrice",
        messages.errorStartPromoPriceMustBeBeforeEndPromoPrice,
        function (value) {
          const { endPromoPrice } = this.parent;
          return (
            !value ||
            !endPromoPrice ||
            new Date(value).getTime() <= new Date(endPromoPrice).getTime()
          );
        }
      )
      .when("promoPrice", (promoPrice, schema) =>
        promoPrice && Number(promoPrice) > 0
          ? schema.required(messages.errorRequiredPromotionStartDate)
          : schema.nullable()
      ),
    endPromoPrice: string()
      .nullable()
      .test(
        "is-after-startPromoPrice",
        messages.errorEndPromoPriceMustBeAfterStartPromoPrice,
        function (value) {
          const { startPromoPrice } = this.parent;
          return (
            !value ||
            !startPromoPrice ||
            new Date(value).getTime() >= new Date(startPromoPrice).getTime()
          );
        }
      )
      .when("promoPrice", (promoPrice, schema) =>
        Number(promoPrice) > 0 ? schema.required(messages.errorRequiredPromotionEndDate) : schema
      ),
  });

export async function checkPromoPriceConditions(
  formData: IListPriceInput,
  messages: ListPriceFormMessages,
  showToast: (toast: ToastProps) => void
) {
  const { errorRequiredPromotionDates, errorPromoPriceGreaterThanZero } = messages;

  const promoPrice = formData.promoPrice ? Number(formData.promoPrice) : 0;
  if (promoPrice > 0 && (!formData.startPromoPrice || !formData.endPromoPrice)) {
    showToast({ text: errorRequiredPromotionDates, type: "error" });
    return true;
  } else if ((formData.startPromoPrice || formData.endPromoPrice) && promoPrice <= 0) {
    showToast({ text: errorPromoPriceGreaterThanZero, type: "error" });
    return true;
  }
  return false;
}
